import axios from "axios";
import { getSessionToken, clearSession } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/webhook/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const PUBLIC_PATHS = new Set([
  "/", "/pricing", "/about", "/contact", "/privacy-policy", "/help",
  "/terms-of-service", "/refund-policy", "/data-deletion",
  "/tabs-guide", "/status-guide", "/analytics-guide", "/catalog-guide",
  "/discounts-guide", "/abandoned-cart-guide", "/team-guide", "/referral-guide",
  "/razorpay-keys-guide", "/stripe-keys-guide", "/paypal-keys-guide",
  "/public-catalog-guide", "/shiprocket-tracking-guide", "/shippo-tracking-guide",
  "/webhooks-guide", "/bulk-image-upload-guide",
]);

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Only the identity check (/me) should be able to force a full
    // logout. A 401 from any other endpoint -- a bug in one specific
    // workflow's auth check, a transient backend hiccup, etc. -- should
    // surface as a normal error to whichever panel made the call (it
    // already has its own toast/catch handling), not wipe out a
    // perfectly valid session just because one unrelated call failed.
    const isIdentityCheck = err?.config?.url === "/me";
    if (err?.response?.status === 401 && isIdentityCheck) {
      clearSession();
      const onPublicPage = PUBLIC_PATHS.has(window.location.pathname);
      if (typeof window !== "undefined" && window.location.pathname !== "/login" && !onPublicPage) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

export function cachedGet(url) {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.timestamp < CACHE_TTL_MS) {
    return Promise.resolve({ data: hit.data });
  }
  return api.get(url).then((r) => {
    cache.set(url, { data: r.data, timestamp: Date.now() });
    return r;
  });
}

export function invalidateCache(prefix) {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

export default api;
